<template>
  <form @submit.prevent="submit" novalidate>
    <!-- Title -->
    <div class="mb-3 mx-2">
      <label for="title" class="bmd-label-float">
        {{ t('gradeSheets.gradeSheetTitle') }}
      </label>
      <input
        type="text"
        class="form-control borderless-input"
        :class="{ 'is-invalid': errors.title }"
        id="title"
        v-model="formData.title"
        :placeholder="t('gradeSheets.enterGradeSheetTitle')"
        dir="auto"
        required
      />
      <div class="invalid-feedback" v-if="errors.title">
        {{ errors.title[0] }}
      </div>
    </div>

    <!-- Academic Year -->
    <div class="mb-3 mx-2">
      <label for="academic_year_id" class="bmd-label-float">{{ t('gradeSheets.academic_year') }}</label>
      <select
        class="form-select borderless-input"
        :class="{ 'is-invalid': errors.academic_year_id }"
        id="academic_year_id"
        v-model="formData.academic_year_id"
        required
      >
        <option value="" disabled>{{ t('gradeSheets.selectAcademicYear') }}</option>
        <option v-for="year in academicYears" :key="year.id" :value="year.id">
          {{ year.name }} ({{ year.year }})
          <span v-if="year.is_active"> - {{ t('common.current') }}</span>
        </option>
      </select>
      <div class="invalid-feedback" v-if="errors.academic_year_id">
        {{ errors.academic_year_id[0] }}
      </div>
    </div>

    <!-- Class -->
    <div class="mb-3 mx-2">
      <label for="class_id" class="bmd-label-float">{{ t('gradeSheets.class') }}</label>
      <select
        class="form-select borderless-input"
        :class="{ 'is-invalid': errors.class_id }"
        id="class_id"
        v-model="formData.class_id"
        required
        @change="() => { fetchClassSubjects(); fetchClassGroups(); }"  
      >
        <option value="" disabled>{{ t('gradeSheets.selectClass') }}</option>
        <option v-for="classItem in classes" :key="classItem.id" :value="classItem.id">
          {{ classItem.name }}
        </option>
      </select>
      <div class="invalid-feedback" v-if="errors.class_id">
        {{ errors.class_id[0] }}
      </div>
    </div>
    
    <!-- Group -->
    <div class="mb-3 mx-2">
      <label for="group_id" class="bmd-label-float">{{ t('gradeSheets.group') }}</label>
      <select
        class="form-select borderless-input"
        :class="{ 'is-invalid': errors.group_id }"
        id="group_id"
        v-model="formData.group_id"
        :disabled="!classGroups.length"
      >
        <option value="">{{ t('gradeSheets.selectGroupOptional') }}</option>
        <option v-for="group in classGroups" :key="group.id" :value="group.id">
          {{ group.name }}
        </option>
      </select>
      <div class="invalid-feedback" v-if="errors.group_id">
        {{ errors.group_id[0] }}
      </div>
      <small class="text-muted" v-if="!classGroups.length && formData.class_id">
        {{ t('gradeSheets.noGroupsForClass') }}
      </small>
    </div>
    
    <!-- Subject -->
    <div class="mb-3 mx-2">
      <label for="subject_id" class="bmd-label-float">{{ t('gradeSheets.subject') }}</label>
      <select
        class="form-select borderless-input"
        :class="{ 'is-invalid': errors.subject_id }"
        id="subject_id"
        v-model="formData.subject_id"
        required
      >
        <option value="" disabled>{{ t('gradeSheets.selectSubject') }}</option>
        <option v-for="subject in classSubjects" :key="subject.id" :value="subject.id">
          {{ subject.name }}
        </option>
      </select>
      <div class="invalid-feedback" v-if="errors.subject_id">
        {{ errors.subject_id[0] }}
      </div>
    </div>

    <!-- Structures -->
    <div class="mb-3 mx-2">
      <label class="bmd-label-float">{{ t('gradeSheets.gradeSheetColumns') }}</label>
      <div class="border rounded p-3">
        <div
          v-for="(structure, index) in formData.structures"
          :key="index"
          class="mb-3"
        >
          <div class="row g-2">
            <div class="col-md-4">
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors[`structures.${index}.column_name`] }"
                v-model="structure.column_name"
                :placeholder="t('gradeSheets.columnName')"
                required
              />
              <div class="invalid-feedback" v-if="errors[`structures.${index}.column_name`]">
                {{ errors[`structures.${index}.column_name`][0] }}
              </div>
            </div>
            <div class="col-md-3">
              <input
                type="number"
                step="0.01"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors[`structures.${index}.full_degree`] }"
                v-model="structure.full_degree"
                :placeholder="t('gradeSheets.fullDegree')"
                required
              />
              <div class="invalid-feedback" v-if="errors[`structures.${index}.full_degree`]">
                {{ errors[`structures.${index}.full_degree`][0] }}
              </div>
            </div>
            <div class="col-md-3">
              <input
                type="color"
                class="form-control form-control-color borderless-input"
                v-model="structure.column_color"
                :title="t('pickColumnColor')"
              />
            </div>
            <div class="col-md-2">
              <button
                type="button"
                class="btn btn-danger btn-sm w-100"
                @click="removeStructure(index)"
                v-if="formData.structures.length > 1"
              >
                <i class="fas fa-trash"></i> {{ t('delete') }}
              </button>
            </div>
          </div>
        </div>
        <button
          type="button"
          class="btn btn-secondary btn-sm mt-2"
          @click="addStructure"
        >
          <i class="fas fa-plus mx-2"></i>{{ t('gradeSheets.addColumn') }}
        </button>
      </div>
    </div>
  </form>
</template>

<script setup>
import { ref, watch, defineExpose, defineProps, defineEmits, onMounted, computed } from "vue";
import { useToast } from "vue-toastification";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import api from "@/api";

const store = useStore();
const toast = useToast();
const { t } = useI18n(); 
const classGroups = ref([]);    
const academicYears = ref([]);
const currentUser = computed(() => store.state.user);
const isTeacher = computed(() => currentUser.value?.role?.slug === "teacher");
const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({
      title: "",
      academic_year_id: "",
      class_id: "",
      group_id: "", 
      subject_id: "",
      structures: [{ column_name: "", full_degree: "", column_color: "#3A416F" }],
    }),
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["submit", "cancel", "validation-error", "update:form-data"]);

const formData = ref({ ...props.initialData });
const errors = ref({});
const classes = ref([]);
const classSubjects = ref([]);
const teacherClasses = ref([]);

const loadAcademicYears = async () => {
  try {
    const response = await api.get('/academic-years');
    academicYears.value = response.data.data;
    
    const currentYear = academicYears.value.find(year => year.is_active);
    if (currentYear && !formData.value.academic_year_id) {
      formData.value.academic_year_id = currentYear.id;
    }
  } catch (error) {
    toast.error(t("gradeForm.toast.fetch_academic_years_failed"));
    academicYears.value = [];
  }
};

const fetchTeacherClasses = async () => {
  if (!isTeacher.value) return;

  try {
    const response = await api.get(`/teachers/${currentUser.value.id}/classes`);
    teacherClasses.value = response.data.data || [];
    classes.value = teacherClasses.value;

  if (teacherClasses.value.length === 1 && !formData.value.class_id) {
  formData.value.class_id = teacherClasses.value[0].id;
  await fetchClassSubjects();
  await fetchClassGroups(); // ✅ مهم
}

  } catch (error) {
    toast.error(t("gradeForm.toast.fetch_teacher_classes_failed"));
    console.error("Error fetching teacher classes:", error);
    teacherClasses.value = [];
    classes.value = [];
  }
};

const fetchClasses = async () => {
  if (isTeacher.value) {
    await fetchTeacherClasses();
    return;
  }

  try {
 const response = await api.get("/classes", {
      params: {
        all: true
      }
    });    classes.value = response.data.data || [];
  } catch (error) {
    toast.error(t("gradeForm.toast.fetch_classes_failed"));
    console.error("Error fetching classes:", error);
    classes.value = [];
  }
};

const fetchClassSubjects = async () => {
  if (!formData.value.class_id) {
    classSubjects.value = [];
    formData.value.subject_id = "";
    return;
  }

  try {
    const response = await api.get(`/classes/${formData.value.class_id}/subjects`);
    classSubjects.value = response.data.data.map((item) => ({
      id: item.subject_id,
      name: item.subject?.name || `Subject ${item.subject_id}`,
    }));

    if (!classSubjects.value.some((sub) => sub.id == formData.value.subject_id)) {
      formData.value.subject_id = "";
    }
  } catch (error) {
    toast.error(t("gradeForm.toast.fetch_subjects_failed"));
    console.error("Error fetching class subjects:", error);
    classSubjects.value = [];
    formData.value.subject_id = "";
  }
};

const validateForm = () => {
  let isValid = true;
  errors.value = {};

  if (!formData.value.title?.trim()) {
    errors.value.title = [t("gradeForm.errors.title_required")];
    isValid = false;
  }

  if (!formData.value.academic_year_id) {
    errors.value.academic_year_id = [t("gradeForm.errors.academic_year_required")];
    isValid = false;
  }

  if (!formData.value.class_id) {
    errors.value.class_id = [t("gradeForm.errors.class_required")];
    isValid = false;
  }

  if (!formData.value.subject_id || formData.value.subject_id === "") {
    errors.value.subject_id = [t("gradeForm.errors.subject_required")];
    isValid = false;
  }

  formData.value.structures.forEach((structure, index) => {
    if (!structure.column_name?.trim()) {
      errors.value[`structures.${index}.column_name`] = [t("gradeForm.errors.column_name_required")];
      isValid = false;
    }

    if (!structure.full_degree || isNaN(structure.full_degree) || parseFloat(structure.full_degree) <= 0) {
      errors.value[`structures.${index}.full_degree`] = [t("gradeForm.errors.full_degree_required")];
      isValid = false;
    }
  });

  if (!isValid) {
    emit("validation-error");
  }

  return isValid;
};

const submit = () => {
  if (validateForm()) {
    emit("submit", formData.value);
  }
};

const setServerErrors = (serverErrors) => {
  errors.value = { ...serverErrors };
};

const addStructure = () => {
  formData.value.structures.push({
    column_name: "",
    full_degree: "",
    column_color: "#3A416F",
  });
};

const removeStructure = (index) => {
  formData.value.structures.splice(index, 1);
};

const fetchClassGroups = async () => {
  classGroups.value = [];
  formData.value.group_id = "";
  if (!formData.value.class_id) return;

  try {
    const res = await api.get(`/classes/${formData.value.class_id}/groups`);

    const rows = res?.data?.data ?? res?.data ?? []; // احتياط لو الapi يرجّع بشكل مختلف

    classGroups.value = rows.map((row) => ({
      id: row.group_id ?? row.id,               // نخلي value = group_id
      name: row.group_name ?? row.name,         // نخلي النص = group_name
      class_group_id: row.id,                   // مفيد لو احتجته لاحقاً
    }));

    if (classGroups.value.length === 1) {
      formData.value.group_id = classGroups.value[0].id;
    }
  } catch (e) {
    classGroups.value = [];
  }
};


watch(
  formData,
  (newVal) => {
    emit("update:form-data", newVal);
  },
  { deep: true }
);

watch(
  () => props.initialData,
  (newData) => {
    formData.value = {
      title: newData.title || "",
      academic_year_id: newData.academic_year_id || "",
      class_id: newData.class_id || "",
      group_id: newData.group_id || newData.class_group_id || "",
      subject_id: newData.subject_id || "",
      structures: newData.structures?.length
        ? [...newData.structures]
        : [{ column_name: "", full_degree: "", column_color: "#3A416F" }],
    };

    if (newData.class_id) {
      fetchClassSubjects().then(() => {
        formData.value.subject_id = newData.subject_id;
      });
    }
    emit("update:form-data", formData.value);
  },
  { deep: true, immediate: true }
);

defineExpose({
  submit,
  setServerErrors,
  validateForm,
  fetchClassSubjects,
});

onMounted(() => {
  loadAcademicYears();
  fetchClasses();
  if (props.initialData.class_id) {
    fetchClassSubjects();
    fetchClassGroups(); 
  }
});
</script>